import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, CustomDropZone, ErrorStateInfo, Grid, Icon, Typography } from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import { PutStartFileProcessingRequest } from '../../../../api-refactored/group-exam-registration/usePutStartFileProcessing';
import { PutStopFileProcessingRequest } from '../../../../api-refactored/group-exam-registration/usePutStopFileProcessing';
import { PutUploadFileToS3Request } from '../../../../api-refactored/s3-upload/usePutUploadFileToS3';
import { GenericErrorResponse } from '../../../../api-refactored/typings';
import { UseCustomMutationReturn } from '../../../../api-refactored/utils/useCustomMutation';
import { FileUploadState } from '../../../../models/File';
import {
  PostUploadPresignedUrlRequest,
  PresignedUrlResponse,
} from '../../../../models/PresignedUrl';
import { formatBytes } from '../../../../utils/FileUtils';
import { ComponentFileProcessingState } from './model';

interface IDocumentUploader {
  classes: any;
  translation: any;
  showUploadInstruction: boolean;
  fileStatus?: FileUploadState;
  apiHooks: {
    uploadMutation: UseCustomMutationReturn<
      void,
      AxiosError<GenericErrorResponse>,
      PutUploadFileToS3Request,
      unknown
    >;
    startFileProcessingMutation: UseCustomMutationReturn<
      void,
      AxiosError<GenericErrorResponse>,
      PutStartFileProcessingRequest,
      unknown
    >;
    getUploadPresignedUrlMutation: UseCustomMutationReturn<
      PresignedUrlResponse,
      AxiosError<GenericErrorResponse>,
      PostUploadPresignedUrlRequest,
      unknown
    >;
    stopFileProcessingMutation: UseCustomMutationReturn<
      void,
      AxiosError,
      PutStopFileProcessingRequest,
      unknown
    >;
  };
  stateHandler: {
    setUploadedFileHandler: {
      uploadedFiles: File[];
      setUploadedFiles: React.Dispatch<React.SetStateAction<File[] | []>>;
    };
    setIsFileProcessedHandler: {
      isFileProcessedSuccess: ComponentFileProcessingState;
      setIsFileProcessedSuccess: React.Dispatch<React.SetStateAction<ComponentFileProcessingState>>;
    };
  };
  isBlacklist?: boolean;
}

const DocumentUploader = ({
  classes,
  translation: t,
  apiHooks,
  stateHandler,
  showUploadInstruction,
  fileStatus,
  isBlacklist = false,
}: IDocumentUploader) => {
  const { uuid: applicationUuid } = useParams<any>();
  const { isError: isUploadError } = apiHooks.uploadMutation;
  const { mutate: uploadPresignedUrlMutateFn } = apiHooks.getUploadPresignedUrlMutation;
  const { uploadedFiles, setUploadedFiles } = stateHandler.setUploadedFileHandler;
  const { setIsFileProcessedSuccess } = stateHandler.setIsFileProcessedHandler;
  const [dropzoneKey, setDropzoneKey] = useState<string>(Date.now().toString());

  const maxFileSize = 314572800;
  const acceptedFileTypes = ['.xlsx'];

  const onFileDelete = () => {
    setUploadedFiles([]);
    setDropzoneKey(Date.now().toString());
  };

  const uploadFileClickHandler = () => {
    apiHooks.stopFileProcessingMutation.reset();
    apiHooks.startFileProcessingMutation.reset();
    setIsFileProcessedSuccess(ComponentFileProcessingState.IDLE);
    uploadPresignedUrlMutateFn({ uuid: applicationUuid, fileName: uploadedFiles[0].name });
  };

  return (
    <>
      {(isUploadError || fileStatus === FileUploadState.FAILED) && (
        <Box className={classes.uploadErrorSection}>
          <Typography variant="body1" className={classes.description}>
            {t('exam->>UploadExcelTemplate.UploadErrorInstruction')}
          </Typography>
          <ErrorStateInfo>
            <Typography variant="body1" className={classes.uploadErrorDescription}>
              {t('exam->>UploadExcelTemplate.UploadErrorDescription')}
            </Typography>
          </ErrorStateInfo>
        </Box>
      )}
      {!isUploadError && showUploadInstruction && (
        <Typography variant="body1" className={classes.description}>
          {t('exam->>UploadExcelTemplate.UploadDocumentsDescription')}
        </Typography>
      )}
      <Grid xs={12} sm={12} md={12} lg={12}>
        <CustomDropZone
          filesLimit={1}
          acceptedFiles={acceptedFileTypes}
          maxSize={maxFileSize}
          className={classes.dropZone}
          key={dropzoneKey}
          onChange={setUploadedFiles}
          initialFiles={uploadedFiles}
          onDelete={onFileDelete}
        />
      </Grid>
      <Box className={classes.dropZoneHelperTextContainer}>
        <Typography className={classes.dropZoneHelperText}>
          <u>
            <b>{t('exam->>UploadExcelTemplate.FileUploadRequirement')}</b>
          </u>
        </Typography>
        <ul>
          <li>
            <Typography className={classes.dropZoneHelperText}>
              {t('exam->>UploadExcelTemplate.AcceptedFileTypes', {
                acceptedFileTypes: '.xlsx',
              })}
            </Typography>
          </li>
          <li>
            <Typography className={classes.dropZoneHelperText}>
              {t('exam->>UploadExcelTemplate.AcceptedFileSize', {
                acceptedFileNum: '1',
                acceptedFileSize: '300MB',
              })}
            </Typography>
          </li>
          <li>
            <Typography className={classes.dropZoneHelperText}>
              {t('exam->>UploadExcelTemplate.FileUploadNotAccept1')}
            </Typography>
          </li>
        </ul>
      </Box>
      {uploadedFiles.length > 0 && (
        <>
          <Typography variant="h5" className={classes.attachedFilesHeader}>
            {t('accessArrangement->>SelectCondition.AttachedFiles')}
          </Typography>
          <Box className={classes.fileBadgeParentContainer}>
            <Grid
              container
              direction="row"
              alignItems="center"
              justify="flex-start"
              wrap="nowrap"
              className={classes.fileBadgeContainer}
            >
              <Box className={classes.iconContainer}>
                <Icon size="M" name={IconName.document} className={classes.documentIcon} />
              </Box>
              <Typography noWrap variant="h5" className={classes.filename}>
                {uploadedFiles[0].name}
              </Typography>
              <Grid container justify="flex-end" className={classes.fileSize}>
                <Typography noWrap variant="body1">
                  {formatBytes(uploadedFiles[0].size)}
                </Typography>
              </Grid>
              <Grid container className={classes.removeCell}>
                <Button
                  className={classes.removeButton}
                  variant="text"
                  onClick={() => onFileDelete()}
                  disabled={false}
                >
                  <IconWithLabel
                    iconName={IconName.trash}
                    iconSize="M"
                    label={t('exam->>UploadExcelTemplate.Remove')}
                    typographyProps={{
                      variant: 'h5',
                      color: 'inherit',
                    }}
                  />
                </Button>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
      {!isBlacklist && (
        <Button
          id="button-upload-file"
          disabled={uploadedFiles.length === 0}
          onClick={uploadFileClickHandler}
        >
          <IconWithLabel
            iconName={IconName.export}
            iconSize="M"
            label={t('exam->>UploadExcelTemplate.UploadFile')}
            typographyProps={{
              variant: 'h5',
              color: 'inherit',
            }}
          />
        </Button>
      )}
    </>
  );
};

export default DocumentUploader;
